import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Settings, Add, RateReview } from '@material-ui/icons'

import { TOAST_TYPE, useTableReducer } from '@base'
import { Pill, Table, TableActionButton, TableActionsWrapper } from '@baseV2'
import { DRAWER_TYPES, strings, TENDERBOOK_SUBSCRIPTION_TYPES, TenderBookUserFilters } from '@constants'
import { getAllTenderBookUsers } from '@data'
import { showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { useForceUpdate } from '@hooks'
import { paths } from '@routes'
import { FullHeightPageLevelTableWrapper } from '@pages/Admin/Tenderbook/styles'
import FollowUpStatusPill from '@pages/Admin/Tenderbook/components/FollowUpStatusPill'
import { isSuperAdminOnly } from 'src/utils/adminFilter'

import { CopyText } from './styles'
import { PrimaryText, SecondaryText, StackCell } from './UserDetails/styles'

const ROWS_PER_PAGE = 10

// Column key → backend `sortBy` enum for server-side sorting.
const SORT_BY_KEYS = {
	created_date: 'CREATED_AT',
	viewed_tender_count: 'VIEWED_TENDER_COUNT',
	viewed_tender_result_count: 'VIEWED_TENDER_RESULT_COUNT',
	viewed_company_count: 'VIEWED_COMPANY_COUNT',
	viewed_ai_synopsis_count: 'VIEWED_AI_SYNOPSIS_COUNT',
}

// Subscription status → v2 Pill colour.
const STATUS_COLOR = {
	[TENDERBOOK_SUBSCRIPTION_TYPES.SUBSCRIBED]: 'success',
	[TENDERBOOK_SUBSCRIPTION_TYPES.TRIAL]: 'warning',
	[TENDERBOOK_SUBSCRIPTION_TYPES.FREE]: 'neutral',
}

const TenderbookUserList = () => {
	const [state, dispatch, actions] = useTableReducer(TenderBookUserFilters)
	const globalDispatch = useDispatch()
	const navigate = useNavigate()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const canPerformActions = isSuperAdminOnly(user?.role)
	const [forceUpdate, update] = useForceUpdate()
	const [searchParams, setSearchParams] = useSearchParams()

	useEffect(() => {
		if (state.loading) return

		dispatch(actions.fetchTableDataProgress())
		getAllTenderBookUsers({ ...state.query.filters, page: state.query.page, size: ROWS_PER_PAGE })
			.then((response) => {
				dispatch(actions.fetchTableDataSuccess(response.data, response.count))
			})
			.catch((error) => {
				dispatch(actions.fetchTableDataFailure(error))
			})
			.finally(() => {})
	}, [state.query, forceUpdate])

	const copyMobileNumber = (e, data) => {
		if (!data) {
			return
		}
		e.stopPropagation()
		navigator.clipboard.writeText(data)
		globalDispatch(
			showToast(true, strings(['mobile', 'number', 'copied']), {
				type: TOAST_TYPE.INFO,
			})
		)
	}

	const columns = useMemo(() => {
		return [
			{
				label: strings('organisation', 'name'),
				key: (row) => row,
				sortable: false,
				mobile: true,
				renderer: (row) => (
					<StackCell>
						<PrimaryText>{row?.company_name || '-'}</PrimaryText>
						<SecondaryText>{row?.pan || '-'}</SecondaryText>
					</StackCell>
				),
			},
			{
				label: strings('name'),
				key: (row) => row,
				sortable: false,
				mobile: true,
				renderer: (row) => (
					<StackCell>
						<PrimaryText>{row?.name || '-'}</PrimaryText>
						<SecondaryText>
							{row?.mobile_no ? <CopyText onClick={(e) => copyMobileNumber(e, row.mobile_no)}>{row.mobile_no}</CopyText> : '-'}
						</SecondaryText>
					</StackCell>
				),
			},
			{ label: strings('gst'), key: 'gst', sortable: false, mobile: true },
			{
				label: strings('status'),
				key: 'subscription_status',
				sortable: false,
				mobile: true,
				renderer: (status) =>
					status ? (
						<Pill color={STATUS_COLOR[status] ?? 'neutral'} size='small'>
							{status}
						</Pill>
					) : (
						'-'
					),
			},
			{
				label: 'Follow up status',
				key: 'follow_up_status',
				sortable: false,
				mobile: true,
				renderer: (status) => <FollowUpStatusPill status={status} />,
			},
			{ label: strings('created', 'at'), key: 'created_date' },
			{ label: strings('first', 'login'), key: 'first_login_date', sortable: false },
			{ label: strings('last', 'login'), key: 'latest_login_date', sortable: false },
			{
				label: 'New Bidder',
				key: (row) => (row?.company_profile?.past_bidder ? 'No' : 'Yes'),
				sortable: false,
				mobile: true,
			},
			{ label: 'Tenders', key: 'viewed_tender_count', align: 'right' },
			{ label: 'Results', key: 'viewed_tender_result_count', align: 'right' },
			{ label: 'Companies', key: 'viewed_company_count', align: 'right' },
			{ label: 'AI Synopsis', key: 'viewed_ai_synopsis_count', align: 'right' },
			canPerformActions && {
				label: strings('actions'),
				align: 'center',
				sortable: false,
				mobile: true,
				key: (row) => row,
				renderer: (row) => (
					<TableActionsWrapper>
						{row?.subscription_status !== TENDERBOOK_SUBSCRIPTION_TYPES.FREE && (
							<TableActionButton title='Settings' onClick={(e) => onSettingsClick(e, row)}>
								<Settings />
							</TableActionButton>
						)}
						<TableActionButton title='Add subscription' onClick={(e) => onAddSubscriptionClick(e, row)}>
							<Add />
						</TableActionButton>
						<TableActionButton title='User remarks' onClick={(e) => onRemarksClick(e, row)}>
							<RateReview />
						</TableActionButton>
					</TableActionsWrapper>
				),
			},
		].filter(Boolean)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canPerformActions])

	// Server-side sort, kept in the URL (flows into the API params via TenderBookUserFilters).
	// Table cycles asc → desc → none; every change goes back to the first page.
	const sortColumnKey = Object.keys(SORT_BY_KEYS).find((key) => SORT_BY_KEYS[key] === searchParams.get('sortBy')) ?? null
	const sortDirection = sortColumnKey ? searchParams.get('sortDirection')?.toLowerCase() ?? null : null

	const onSortHandler = (key, direction) => {
		const params = Object.fromEntries(searchParams.entries())
		delete params.sortBy
		delete params.sortDirection
		if (direction && SORT_BY_KEYS[key]) {
			params.sortBy = SORT_BY_KEYS[key]
			params.sortDirection = direction.toUpperCase()
		}
		setSearchParams({ ...params, page: 0 })
	}

	const changePageHandler = (next = true) => {
		dispatch(actions.changePage(next))
	}

	// The row is handed to the detail layout so its tabs paint without waiting on a refetch.
	const onRowClickHandle = (row) => {
		if (!row?.user_id) return
		navigate(paths.tenderbook_user_basic_details(row.user_id), { state: { user: row } })
	}

	// Gear icon (rendered for SUBSCRIBED and TRIAL users) — opens the user settings drawer.
	const onSettingsClick = (e, row) => {
		e.stopPropagation()
		globalDispatch(toggleDrawer(true, DRAWER_TYPES.TENDERBOOK_USER_SETTINGS, { label: 'User settings', update, data: row }))
	}

	// "+" icon — opens the "Update subscription status" drawer.
	const onAddSubscriptionClick = (e, row) => {
		e.stopPropagation()
		globalDispatch(toggleDrawer(true, DRAWER_TYPES.TENDERBOOK_UPDATE_SUBSCRIPTION, { label: 'Update subscription status', update, data: row }))
	}

	// Review icon — opens the "User remarks" drawer (follow up status + comments).
	const onRemarksClick = (e, row) => {
		e.stopPropagation()
		globalDispatch(toggleDrawer(true, DRAWER_TYPES.TENDERBOOK_USER_REMARKS, { label: 'User remarks', update, data: row }))
	}

	return (
		<FullHeightPageLevelTableWrapper>
			<Table
				data={state.data}
				columns={columns}
				loading={state.loading}
				rowsToDisplay={ROWS_PER_PAGE}
				totalItemCount={state.total_count}
				page={state.query.page}
				onPageChange={changePageHandler}
				sortBy={sortColumnKey}
				sortDirection={sortDirection}
				onSort={onSortHandler}
				onRowClick={onRowClickHandle}
				emptyState={{ text: 'No users found' }}
				scrollable
			/>
		</FullHeightPageLevelTableWrapper>
	)
}

export default TenderbookUserList
